import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef
} from 'react'

import { gsap, ScrollTrigger } from '@/lib/gsap'

type ScrollTarget = string | number | HTMLElement

interface ScrollOptions {
    offset?: number
    immediate?: boolean
    duration?: number
    onComplete?: () => void
}

interface SmoothScrollValue {
    scrollTo: (target: ScrollTarget, options?: ScrollOptions) => void
    lock: () => void
    unlock: () => void
    /** Re-measures the page, needed after the content is swapped by a route change. */
    resize: () => void
}

interface SmoothScrollProviderProps {
    children: ReactNode
}

const SmoothScrollContext = createContext<SmoothScrollValue>({
    scrollTo: () => undefined,
    lock: () => undefined,
    unlock: () => undefined,
    resize: () => undefined
})

const resolveTarget = (target: ScrollTarget): HTMLElement | number | null => {
    if (typeof target === 'number') return target
    if (typeof target === 'string') return document.querySelector<HTMLElement>(target)
    return target
}

export const SmoothScrollProvider: FC<SmoothScrollProviderProps> = ({ children }): ReactNode => {
    const lenisRef = useRef<Lenis | null>(null)
    const lockCount = useRef(0)

    useEffect(() => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const coarsePointer = window.matchMedia('(pointer: coarse)').matches

        if (reduceMotion || coarsePointer) return

        const lenis = new Lenis({
            duration: 1.2,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true
        })

        lenisRef.current = lenis
        if (lockCount.current > 0) lenis.stop()

        lenis.on('scroll', ScrollTrigger.update)

        const tick = (time: number): void => {
            lenis.raf(time * 1000)
        }

        gsap.ticker.add(tick)
        gsap.ticker.lagSmoothing(0)

        return () => {
            gsap.ticker.remove(tick)
            gsap.ticker.lagSmoothing(500, 33)
            lenis.destroy()
            lenisRef.current = null
        }
    }, [])

    const scrollTo = useCallback((target: ScrollTarget, options: ScrollOptions = {}): void => {
        const resolved = resolveTarget(target)
        if (resolved === null) return

        const offset = options.offset ?? 0
        const lenis = lenisRef.current

        if (lenis) {
            lenis.scrollTo(resolved, {
                offset,
                immediate: options.immediate,
                duration: options.duration ?? 1.6,
                force: true,
                onComplete: options.onComplete ? () => options.onComplete?.() : undefined
            })
            return
        }

        const top = typeof resolved === 'number'
            ? resolved
            : resolved.getBoundingClientRect().top + window.scrollY + offset
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top, behavior: options.immediate || reduceMotion ? 'auto' : 'smooth' })

        if (options.onComplete) window.setTimeout(options.onComplete, options.immediate ? 0 : 700)
    }, [])

    const lock = useCallback((): void => {
        lockCount.current += 1
        lenisRef.current?.stop()
        document.documentElement.style.overflow = 'hidden'
    }, [])

    const unlock = useCallback((): void => {
        lockCount.current = Math.max(0, lockCount.current - 1)
        if (lockCount.current > 0) return

        lenisRef.current?.start()
        document.documentElement.style.overflow = ''
    }, [])

    const resize = useCallback((): void => {
        lenisRef.current?.resize()
    }, [])

    const value = useMemo(() => ({ scrollTo, lock, unlock, resize }), [scrollTo, lock, unlock, resize])

    return (
        <SmoothScrollContext.Provider value={value}>
            {children}
        </SmoothScrollContext.Provider>
    )
}

export const useSmoothScroll = (): SmoothScrollValue => useContext(SmoothScrollContext)
